import React, { useState, useEffect, useRef } from 'react';
import { generateInviteUrl } from 'utils/inviteLinks';
import styles from './styles.module.scss';

// Component for the copy icon
const CopyIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
        <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
    </svg>
);

// Component for the checkmark icon
const CheckmarkIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
    </svg>
);

const GroupInviteLinkView = (props) => {
    const { isShowing, onClose, inviteLink, saveInviteLink, removeInviteLink, isLoading, teamId, groupId, teamName, groupName } = props;
    
    // State for form fields
    const [inviteCode, setInviteCode] = useState('');
    const [passcode, setPasscode] = useState('');
    const [numInvitations, setNumInvitations] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [justCopied, setJustCopied] = useState(false);
    
    // Ref for date input to programmatically open calendar
    const dateInputRef = useRef(null);

    // Chat widget control functions
    const hideChatWidget = () => {
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.remove();
        }
        window.hsConversationsOnReady = window.hsConversationsOnReady || [];
        window.hsConversationsOnReady.push(() => {
            if (window.HubSpotConversations) {
                window.HubSpotConversations.widget.remove();
            }
        });
        const style = document.createElement('style');
        style.id = 'hide-chat-widget-style';
        style.textContent = `#hubspot-messages-iframe-container { display: none !important; visibility: hidden !important; }`;
        document.head.appendChild(style);
    };

    const showChatWidget = () => {
        if (window.HubSpotConversations) {
            window.HubSpotConversations.widget.load();
        }
        const style = document.getElementById('hide-chat-widget-style');
        if (style) {
            style.remove();
        }
    };
    
    // Handle dialog closing - create a wrapped version that resets form state
    const handleClose = () => {
        // Show chat widget when closing dialog
        showChatWidget();
        // Restore body scroll when modal closes
        const scrollY = document.body.style.top;
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        if (scrollY) {
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }
        // No need to reset form state here as it's handled by the useEffect
        // Just call the original onClose
        onClose();
    };
    
    // Debug props values with more detail
    useEffect(() => {
        console.log('GroupInviteLinkView received props:', {
            groupId,
            teamId,
            teamName: teamName || '(empty string)',
            teamNameType: typeof teamName,
            teamNameLength: teamName ? teamName.length : 0,
            groupName: groupName || '(empty string)',
            isShowing
        });
    }, [groupId, teamId, teamName, groupName, isShowing]);

    // Hide/show chat widget based on dialog visibility
    useEffect(() => {
        if (isShowing) {
            hideChatWidget();
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            showChatWidget();
            // Restore body scroll when modal closes
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        }

        // Cleanup: show chat widget and restore scroll when component unmounts
        return () => {
            if (isShowing) {
                showChatWidget();
                // Restore body scroll
                const scrollY = document.body.style.top;
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.width = '';
                document.body.style.top = '';
                if (scrollY) {
                    window.scrollTo(0, parseInt(scrollY || '0') * -1);
                }
            }
        };
    }, [isShowing]);
    
    // Effect to reset form when the dialog visibility changes or group ID changes
    useEffect(() => {
        console.log('[Dialog] Visibility or group changed:', { isShowing, groupId });
        
        // Always reset the form when the dialog opens or closes, or when the group changes
        setInviteCode('');
        setPasscode('');
        setNumInvitations('');
        setExpirationDate('');
        
        // Don't populate with invite link data if dialog is not showing
        if (!isShowing) return;
        
        console.log('[Dialog] Dialog showing for group:', groupId);
    }, [isShowing, groupId]);
    
    // Separate effect to populate form with invite link data when it becomes available
    useEffect(() => {
        // Skip if dialog is not showing or there's no invite link
        if (!isShowing || !inviteLink) return;
        
        console.log('[Dialog] Populating form with invite link:', inviteLink);
        
        // Reset the copy button state whenever a new link is loaded
        setJustCopied(false);
        
        // Only update the form if we have an invite link with matching group ID
        if (inviteLink.group_id !== groupId) {
            console.log('[Dialog] Skipping invite link from different group:', inviteLink.group_id, groupId);
            return;
        }
        
        // Set invite code if it exists
        if (inviteLink.invite_code) {
            console.log('[Dialog] Setting invite code to:', inviteLink.invite_code);
            setInviteCode(inviteLink.invite_code);
        }
        
        // Set passcode if it exists
        if (inviteLink.passcode) {
            console.log('[Dialog] Setting passcode to:', inviteLink.passcode);
            setPasscode(inviteLink.passcode);
        }
        
        // Set max uses if it exists and is not null
        if (inviteLink.max_uses !== null && inviteLink.max_uses !== undefined) {
            setNumInvitations(inviteLink.max_uses);
        } else {
            setNumInvitations(''); // Empty string for unlimited invitations
        }
        
        // Set expiration date if it exists
        if (inviteLink.expires_at) {
            // Convert the date to the format expected by the date input
            console.log('[Dialog] Setting expiration date from:', inviteLink.expires_at);
            const expiryDate = new Date(inviteLink.expires_at);
            
            // Format using local date methods instead of toISOString() to avoid timezone issues
            const year = expiryDate.getFullYear();
            const month = String(expiryDate.getMonth() + 1).padStart(2, '0'); // +1 because months are 0-indexed
            const day = String(expiryDate.getDate()).padStart(2, '0');
            const formattedExpiryDate = `${year}-${month}-${day}`;
            
            console.log('[Dialog] Formatted date for input:', formattedExpiryDate);
            setExpirationDate(formattedExpiryDate);
        } else {
            // Don't set a default expiration date
            setExpirationDate('');
        }
    }, [isShowing, inviteLink, groupId]);
    
    // Always render the dialog when isShowing is true, regardless of other state
    if (!isShowing) return null;
    
    // Function to get the full invite URL
    const getFullInviteUrl = () => {
        if (!inviteLink || !inviteLink.invite_code) return '';
        return generateInviteUrl(inviteLink.invite_code);
    };
    
    // Function to copy the full invite URL to clipboard
    const copyToClipboard = () => {
        if (inviteLink && inviteLink.invite_code) {
            const fullUrl = getFullInviteUrl();
            navigator.clipboard.writeText(fullUrl)
                .then(() => {
                    // Show checkmark icon
                    setJustCopied(true);
                    
                    // Reset back to copy icon after 1 second
                    setTimeout(() => {
                        setJustCopied(false);
                    }, 1000);
                })
                .catch(err => console.error('Could not copy text: ', err));
        }
    };
    
    const handleSave = async () => {
        // Trim values first and update state
        const trimmedInviteCode = inviteCode ? inviteCode.trim() : '';
        const trimmedPasscode = passcode ? passcode.trim() : '';
        
        setInviteCode(trimmedInviteCode);
        setPasscode(trimmedPasscode);
        
        // Validate with trimmed values
        if (trimmedInviteCode && !/^[a-zA-Z0-9]+$/.test(trimmedInviteCode)) {
            alert('Invite code must contain only letters and numbers');
            return;
        }
        
        // Validate passcode if provided
        if (trimmedPasscode) {
            if (trimmedPasscode.length < 4) {
                alert('Passcode must be at least 4 characters');
                return;
            }
            
            if (!/^[a-zA-Z0-9]+$/.test(trimmedPasscode)) {
                alert('Passcode must contain only letters and numbers');
                return;
            }
        }
        
        // Validate group ID is available
        if (!groupId) {
            alert('Cannot create invite: No group selected');
            return;
        }
        
        // Get max uses as an integer or null for unlimited
        let maxUses = null;
        if (numInvitations !== '' && numInvitations !== null && numInvitations !== undefined) {
            maxUses = parseInt(numInvitations, 10);
            if (isNaN(maxUses) || maxUses <= 0) {
                maxUses = null; // Reset to null if invalid
            }
        }
        
        // Calculate expiration days or null for no expiration
        let expiryDays = null;
        if (expirationDate && expirationDate.trim() !== '') {
            expiryDays = calculateDaysFromDate(expirationDate);
        }
        
        // Create an object with the form data
        const data = {
            group_id: groupId,
            max_uses: maxUses,
            expires_in_days: expiryDays
        };
        
        // Add custom invite code if provided
        if (trimmedInviteCode) {
            data.invite_code = trimmedInviteCode;
        }
        
        // Always include passcode in updates (empty string will disable the passcode)
        // The backend will convert empty string to null
        data.passcode = trimmedPasscode;
        
        // If editing existing invite link, include the ID
        if (inviteLink && inviteLink.id) {
            data.id = inviteLink.id;
        }
        
        // Log data for debugging
        const isUpdating = inviteLink && inviteLink.invite_code;
        
        try {
            if (isUpdating) {
                console.debug('Updating existing group invite link:', inviteLink.invite_code, data);
                await props.updateInviteLink(inviteLink.invite_code, data);
            } else {
                console.debug('Creating new group invite link with data:', data);
                await saveInviteLink(data);
            }
            
            // Close the dialog after successful save
            console.log('Save completed, closing dialog');
            handleClose();
        } catch (error) {
            console.error('Error saving invite link:', error);
            // Dialog remains open if there's an error
        }
    };
    
    const handleRemove = () => {
        if (inviteLink && inviteLink.invite_code) {
            console.log('[Dialog] Deactivating invite link:', inviteLink);
            
            // First clear all form fields to ensure no stale data is used
            setInviteCode('');
            setPasscode('');
            setNumInvitations('');
            setExpirationDate('');
            
            // Then remove the link (pass the entire object, not just the ID)
            removeInviteLink(inviteLink);
        } else {
            console.error('[Dialog] Cannot deactivate link: Missing invite_code', inviteLink);
        }
    };
    
    const calculateDaysFromDate = (dateString) => {
        // Check if date is empty
        if (!dateString || dateString.trim() === '') {
            return null;
        }
        
        try {
            // Parse the input date string to a Date object
            const inputDate = new Date(dateString);
            
            // Check if date is valid
            if (isNaN(inputDate.getTime())) {
                console.error('[Dialog] Invalid date provided:', dateString);
                return null;
            }
            
            // Get today's date at midnight (to compare just the dates, not times)
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // Calculate the difference in milliseconds
            const diffMs = inputDate.getTime() - today.getTime();
            
            // Convert to days and round up to handle partial days
            const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
            
            // Ensure we have at least 1 day of validity
            return Math.max(1, diffDays);
        } catch (error) {
            console.error('[Dialog] Error calculating days from date:', error);
            return null;
        }
    };
    
    const handleDateChange = (e) => {
        setExpirationDate(e.target.value);
    };
    
    // Function to generate a random 4-digit passcode
    const generatePasscode = () => {
        const randomCode = Math.floor(1000 + Math.random() * 9000);
        setPasscode(randomCode.toString());
    };
    
    // Calculate remaining invites based on max_uses and use_count
    const getRemainingInvites = () => {
        if (!inviteLink) return 0;
        
        // If max_uses is null, it means unlimited invites
        if (inviteLink.max_uses === null) {
            return 'unlimited';
        }
        
        // If max_uses is defined, calculate remaining
        if (inviteLink.max_uses) {
            return Math.max(0, inviteLink.max_uses - (inviteLink.use_count || 0));
        }
        
        return 0;
    };
    
    // Calculate number of athletes that joined using this link
    const getJoinedAthletes = () => {
        if (!inviteLink) return 0;
        return inviteLink.use_count || 0;
    };
    
    // Calculate used and remaining invitations
    const usedInvitations = inviteLink && inviteLink.used_count ? inviteLink.used_count : 0;
    const remainingInvitations = numInvitations - usedInvitations;
    
    // Don't render anything if dialog is not showing
    if (!isShowing) return null;
    
    // Handle modal overlay events to prevent background scrolling
    const handleModalClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
    };

    const handleModalContentClick = (e) => {
        e.stopPropagation();
    };

    const handleTouchMove = (e) => {
        // Allow scrolling within the modal content
        e.stopPropagation();
    };

    return (
        <div
            className={styles.modal}
            onClick={handleModalClick}
            onTouchMove={handleTouchMove}
        >
            <div
                className={styles.modalContent}
                onClick={handleModalContentClick}
                onTouchMove={handleTouchMove}
            >
                <div className={styles.header}>
                    <h2 className={styles.title}>Invite Link</h2>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        type="button"
                        title="Close dialog"
                    >
                        ✕
                    </button>
                </div>
                
                <div className={styles.formLayout}>
                    {/* URL and Passcode in the same row */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Invite code <span className={styles.optionalText}>(Optional)</span></label>
                            <div className={styles.inputWithButton}>
                                <input
                                    type="text"
                                    value={inviteCode}
                                    onChange={e => !inviteLink && setInviteCode(e.target.value)}
                                    readOnly={!!inviteLink}
                                    className={`${styles.urlInput} ${inviteLink ? styles.readOnlyInput : ''}`}
                                    placeholder="Custom code"
                                    pattern="[a-zA-Z0-9]+"
                                    title="Invite code must contain only letters and numbers"
                                />
                                <button
                                    onClick={copyToClipboard}
                                    className={styles.copyButton}
                                    title="Copy full URL to clipboard"
                                >
                                    {justCopied ? <CheckmarkIcon /> : <CopyIcon />}
                                </button>
                            </div>
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Passcode <span className={styles.optionalText}>(Optional)</span></label>
                            <div className={styles.inputWithButton}>
                                <input 
                                    type="text"
                                    className={styles.urlInput}
                                    value={passcode}
                                    onChange={e => setPasscode(e.target.value)}
                                    placeholder="4-digit code"
                                    pattern="[a-zA-Z0-9]+"
                                    title="Passcode must contain only letters and numbers"
                                />
                                <button 
                                    className={styles.generateButton}
                                    onClick={generatePasscode}
                                >
                                    Generate
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    {/* Team and Group in the same row */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Team</label>
                            <div className={styles.displayField}>
                                {teamName ? teamName : <span className={styles.placeholderText}>(None)</span>}
                            </div>
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Group</label>
                            <div className={styles.displayField}>
                                {groupName ? groupName : <span className={styles.placeholderText}>(None)</span>}
                            </div>
                        </div>
                    </div>
                    
                    {/* Number of Invitations and Expiration Date */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Number of Invitations <span className={styles.optionalText}>(Optional)</span></label>
                            <input 
                                type="number"
                                className={styles.formControl}
                                value={numInvitations}
                                onChange={e => setNumInvitations(e.target.value)}
                                min="1"
                                placeholder="Unlimited"
                            />
                        </div>
                        
                        <div className={styles.formGroup}>
                            <label className={styles.label}>Expiration Date <span className={styles.optionalText}>(Optional)</span></label>
                            <div className={styles.expirationContainer}>
                                <div className={styles.dateInputWrapper}>
                                    <input
                                        type="date"
                                        className={styles.formControl}
                                        value={expirationDate}
                                        onChange={handleDateChange}
                                        ref={dateInputRef}
                                    />
                                    {expirationDate && (
                                        <button
                                            type="button"
                                            className={styles.clearDateButton}
                                            onClick={() => setExpirationDate('')}
                                            title="Remove expiration date"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Usage statistics */}
                    {inviteLink && (
                        <div className={styles.statsContainer}>
                            <strong>{getJoinedAthletes()}</strong> new athletes joined using this invite link. 
                            {getRemainingInvites() === 'unlimited' ? (
                                <span> Invites remaining: <strong>Unlimited</strong></span>
                            ) : (
                                <span> There are <strong>{getRemainingInvites()}</strong> invites remaining.</span>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Buttons with consistent styling */}
                <div className={styles.buttonsContainer}>
                    <div className={styles.actionButtons}>
                        {inviteLink && (
                            <button
                                className={`${styles.button} ${styles.dangerButton}`}
                                onClick={handleRemove}
                                disabled={isLoading}
                            >
                                Remove Invite Code
                            </button>
                        )}

                        <button
                            className={`${styles.button} ${styles.primaryButton}`}
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            Save Invite Code
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GroupInviteLinkView;
